import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import { Tabs, Tab } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { push } from "connected-react-router";
import { Route } from "react-router-dom";
import {
  validate,
  buildValidateOrderDetailsRoute,
  buildValidateOrderAssignmentsRoute,
  validateOrderDetails,
  validateOrderAssignments
} from "../../routes/AdminRoutes";
import ValidateOrderDetails from "./ValidateOrderDetailsContainer";
import ValidateOrderAssignments from "./ValidateOrderAssignments";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import useResource from "../../hooks/useResource";
import { useValidateOrders } from "../../hooks/validateOrders";
import { validateOrderStatuses } from "../../constants";
import {
  enqueueSuccessSnackbar,
  enqueueWarningSnackbar,
  enqueueErrorSnackbar
} from "../../redux/actions/snackbarActions";

const RESOURCE_FIELDS = {
  order: [
    "validateOrderRecommendedUpskillTrainings",
    "orderAssignments",
    "shopType",
    "location",
    "surveyEmployees",
    "employee",
    "orderedBy",
    "adjustedEmployee",
    "employeeIdAdjustedBy",
    "scoreAdjustedBy",
    "commentsAdjustedBy",
    "status",
    "timeRange",
    "scoreAdjusted",
    "scoreAdjustedAt",
    "employeeIdAdjustedAt",
    "commentsAdjustedAt",
    "surveyCommentsAdjustedAt",
    "surveyCommentsAdjustedBy",
    "orderType",
    "comments",
    "surveyComments",
    "createdAt",
    "completedAt",
    "score",
    "surveyUrl",
    "surveyUser",
    "surveyEmployee",
    "skippedAssignments"
  ],
  user: ["fullName"],
  location: ["name"],
  shop_type: ["description"],
  recommendedTraining: ["surveyDetail", "skill", "trainingObjects"],
  category: ["trainingObjects"],
  trainingObject: ["name", "daysToComplete"],
  skill: ["name", "categories"],
  orderAssignment: ["skill", "trainingObject", "custom", "daysToComplete"]
};

const RESOURCE_INCLUDE = [
  "validateOrderRecommendedUpskillTrainings",
  "validateOrderRecommendedUpskillTrainings.surveyDetail",
  "validateOrderRecommendedUpskillTrainings.skill",
  "validateOrderRecommendedUpskillTrainings.trainingObjects",
  "validateOrderRecommendedUpskillTrainings.trainingObjects.customerAssignments",
  "orderAssignments",
  "orderAssignments.skill",
  "orderAssignments.trainingObject",
  "adjustedEmployee",
  "surveyEmployees",
  "shopType",
  "location",
  "employee",
  "orderedBy",
  "employeeIdAdjustedBy",
  "commentsAdjustedBy",
  "surveyCommentsAdjustedBy",
  "scoreAdjustedBy"
];

const RESOURCE_OPTIONS = {
  fields: RESOURCE_FIELDS,
  include: RESOURCE_INCLUDE
};

const ValidateOrderContainer = ({
  match: {
    params: { orderId }
  },
  location: { pathname }
}) => {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(0);
  const { t } = useTranslation();

  const { getValidateOrder, updateValidateOrder } = useValidateOrders();

  const apiCallbacks = useMemo(
    () => ({
      getOrder: () => getValidateOrder(orderId, RESOURCE_OPTIONS),
      updateOrder: data => updateValidateOrder(orderId, data, RESOURCE_OPTIONS),
      assignOrder: assignments =>
        updateValidateOrder(
          orderId,
          {
            assignments,
            type: "Validate::Order",
            id: orderId,
            status: validateOrderStatuses.completed
          },
          RESOURCE_OPTIONS
        )
          .then(res => {
            if (res.errors) throw res.errors;
            if (res.data.skippedAssignments?.length > 0) {
              dispatch(
                enqueueWarningSnackbar({
                  message: t(
                    "validate.order.assignments.snackbar.assignPartialMsg",
                    {
                      assignments: res.data.skippedAssignments
                    }
                  )
                })
              );
            } else {
              dispatch(
                enqueueSuccessSnackbar({
                  message: t(
                    "validate.order.assignments.snackbar.assignSuccessMsg"
                  )
                })
              );
            }

            return res;
          })
          .catch(error => {
            dispatch(
              enqueueErrorSnackbar({
                message: t(
                  "validate.order.assignments.snackbar.assignCustomErrorMsg"
                )
              })
            );
            throw error;
          }),
      assignCustomAssignments: custom_assignments =>
        updateValidateOrder(
          orderId,
          {
            custom_assignments,
            type: "Validate::Order",
            id: orderId
          },
          RESOURCE_OPTIONS
        )
          .then(res => {
            if (res.errors) throw res.errors;
            dispatch(
              enqueueSuccessSnackbar({
                message: t(
                  "validate.order.assignments.snackbar.assignCustomSuccessMsg"
                )
              })
            );
            return res;
          })
          .catch(error => {
            dispatch(
              enqueueErrorSnackbar({
                message: t(
                  "validate.order.assignments.snackbar.assignCustomErrorMsg"
                )
              })
            );
            throw error;
          }),
      reviewOrder: () =>
        updateValidateOrder(
          orderId,
          {
            type: "Validate::Order",
            id: orderId,
            status: validateOrderStatuses.completed
          },
          RESOURCE_OPTIONS
        )
    }),
    [orderId]
  );

  useEffect(() => {
    getOrder();
  }, [orderId]);

  const [
    { data: order },
    {
      getOrder,
      updateOrder,
      assignOrder,
      assignCustomAssignments,
      reviewOrder
    },
    validateAction
  ] = useResource(apiCallbacks);

  const isUpdatingOrder = [
    updateOrder,
    assignOrder,
    reviewOrder,
    assignCustomAssignments
  ].includes(validateAction);
  const isLoading = validateAction !== null;
  const assignmentsAvailable =
    !!order &&
    [
      validateOrderStatuses.readyForReview,
      validateOrderStatuses.readyForSupervisorReview,
      validateOrderStatuses.completed
    ].includes(order.status);

  const orderTabs = [
    {
      route: buildValidateOrderDetailsRoute(orderId),
      title: "validate.order.tabs.details"
    },
    {
      route: buildValidateOrderAssignmentsRoute(orderId),
      title: "validate.order.tabs.assignments",
      disabled: !assignmentsAvailable
    }
  ];

  const handleGoBack = () => {
    dispatch(push(validate));
  };

  function handleTabChange(newTab) {
    dispatch(push(orderTabs[newTab].route));
    setCurrentTab(newTab);
  }

  useEffect(() => {
    handleTabChange(
      Math.max(
        orderTabs.findIndex(tab => tab.route === pathname),
        0
      )
    );
  }, []);

  return (
    <AdminPageLayout
      title={
        <Breadcrumb
          routes={["Validate", t("validate.order.routeTitle") + " " + orderId]}
        />
      }
      onGoBackClick={handleGoBack}
    >
      <Tabs
        value={currentTab}
        onChange={(e, newTab) => handleTabChange(newTab)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons={false}
      >
        {orderTabs.map((tab, index) => (
          <Tab key={index} label={t(tab.title)} disabled={tab.disabled} />
        ))}
      </Tabs>
      <Route
        exact
        path={validateOrderDetails}
        render={() => (
          <ValidateOrderDetails
            order={order}
            updateOrder={updateOrder}
            isUpdatingOrder={isUpdatingOrder}
            isLoading={isLoading}
          />
        )}
      />
      <Route
        exact
        path={validateOrderAssignments}
        render={() => (
          <ValidateOrderAssignments
            order={order}
            assignOrder={assignOrder}
            assignCustomAssignments={assignCustomAssignments}
            isUpdatingOrder={isUpdatingOrder}
            reviewOrder={reviewOrder}
            isLoading={isLoading}
          />
        )}
      />
    </AdminPageLayout>
  );
};

ValidateOrderContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The order ID for the order
       */
      orderId: PropTypes.string
    }),
    path: PropTypes.string,
    url: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};

export default ValidateOrderContainer;
